// ==============================
// Imports
// ==============================

// MUI Components
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ClassIcon from '@mui/icons-material/School';
import StorageIcon from '@mui/icons-material/Storage';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import UserGuideIcon from '@mui/icons-material/MenuBook';

// Libraries
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

// Local components
import { useTranslation } from '../i18n';
import UserMenu from './UserMenu';
import DarkModeToggle from './common/DarkModeToggle';
import LanguageSwitcher from './common/LanguageSwitcher';

// ==============================
// Types
// ==============================
interface TabItem {
  id: string | number;
  img?: React.ReactNode;
  label: string;
  labelKey?: string;
  path?: string;
  children?: TabItem[];
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const drawerWidth = 240;

// ==============================
// Styles
// ==============================
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

// ==============================
// Components
// ==============================
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ==============================
// Render
// ==============================
export default function MiniDrawer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  type TabUrl = {
    path: string;
  };

  const theme = useTheme();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const navLabel = (item: TabItem) =>
    item.labelKey ? t(item.labelKey) : item.label;

  const tabClickEvent = (url: TabUrl) => {
    navigate(url.path);
  };

  const dataRes: TabItem[] = [
    {
      id: 'assessment-home',
      label: 'Assessment',
      labelKey: 'nav.assessment',
      img: <AssessmentIcon />,
      path: '/assessment-home',
    },
    {
      id: 'coach-class',
      label: 'Class Management',
      labelKey: 'nav.classManagement',
      img: <ClassIcon />,
      path: '/coach-class',
    },
    {
      id: 'coach-master-data',
      label: 'Master Data',
      labelKey: 'nav.masterData',
      img: <StorageIcon />,
      path: '/coach-master-data',
    },
    {
      id: 'pending-enrollment',
      label: 'Pending Enrollment',
      labelKey: 'nav.pendingEnrollment',
      img: <PendingActionsIcon />,
      path: '/pending-enrollment',
    },
    {
      id: 'user-guide',
      label: 'User Guide',
      labelKey: 'nav.userGuide',
      img: <UserGuideIcon />,
      path: '/user-guide',
    },
  ];

  // Setting Collapse
  const [openCollapse, setOpenCollapse] = React.useState(false);
  const handleCollapse = () => {
    setOpenCollapse(!openCollapse);
  };

  const user = JSON.parse(
    localStorage.getItem('aquatics_hub_2025_user') || 'null',
  );
  const handleLogout = () => {
    localStorage.removeItem('aquatics_hub_2025_tokens');
    localStorage.removeItem('aquatics_hub_2025_user');
    navigate('/signin');
  };

  return (
    <>
      <Box>
        <CssBaseline />
        <AppBar position="fixed" open={open} style={{ background: '#4da7db' }}>
          <Toolbar>
            {/* Menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(!open)}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon style={{ color: 'white' }} />
            </IconButton>
            {/* Official logo */}
            <Box
              component="img"
              src={
                theme.palette.mode === 'dark'
                  ? '/Bluename_edited_white.avif'
                  : '/Bluename_edited.avif'
              }
              alt="Aquatics HUB"
              sx={{
                height: 40,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />

            {/* Right side: language, theme, user */}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LanguageSwitcher />
              <DarkModeToggle />
              <UserMenu username={user?.name || ''} onLogout={handleLogout} />
            </Box>
          </Toolbar>
          {/* Other item */}
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <IconButton onClick={() => setOpen(!open)}>
              {theme.direction === 'rtl' ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {dataRes.map((text: TabItem) => {
              if (text.children)
                return (
                  <React.Fragment key={text.id}>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                        }}
                        onClick={handleCollapse}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {text.img}
                        </ListItemIcon>
                        <ListItemText
                          primary={navLabel(text)}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                        {openCollapse ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={openCollapse} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {text.children.map((child: TabItem) => (
                          <ListItem
                            key={child.id}
                            disablePadding
                            sx={{ display: 'block' }}
                          >
                            <ListItemButton
                              sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                pr: 2.5,
                                pl: 5,
                              }}
                              onClick={() => tabClickEvent(child as TabUrl)}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: open ? 3 : 'auto',
                                  justifyContent: 'center',
                                }}
                              >
                                {child.img}
                              </ListItemIcon>
                              <ListItemText
                                primary={navLabel(child)}
                                sx={{ opacity: open ? 1 : 0 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              else
                return (
                  <ListItem
                    key={text.id}
                    disablePadding
                    sx={{ display: 'block' }}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                      }}
                      onClick={() => tabClickEvent(text as TabUrl)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {text.img}
                      </ListItemIcon>
                      <ListItemText
                        primary={text.label}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
            })}
          </List>
        </Drawer>
      </Box>
    </>
  );
}
